import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import {
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  CheckSquare,
  Square,
  Building2,
  User,
  Loader,
  Calendar,
  Download,
  X,
  Edit2,
  Tag
} from 'lucide-react';
import FeeConfigurationPanel from './FeeConfigurationPanel';
import FeeOverrideModal from './FeeOverrideModal';

const PayoutsTab = () => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, partner, organization
  const [activeSection, setActiveSection] = useState('candidates'); // candidates or history
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [payoutNotes, setPayoutNotes] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedPayoutForCompletion, setSelectedPayoutForCompletion] = useState(null);
  const [manualReference, setManualReference] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedPayoutForCancellation, setSelectedPayoutForCancellation] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // Fee configuration state - load from database, don't hardcode defaults
  const [feeConfig, setFeeConfig] = useState(null);
  const [loadingFeeConfig, setLoadingFeeConfig] = useState(true);
  const [recipientFeeOverrides, setRecipientFeeOverrides] = useState({});
  const [showFeeOverrideModal, setShowFeeOverrideModal] = useState(false);
  const [selectedRecipientForFeeOverride, setSelectedRecipientForFeeOverride] = useState(null);

  useEffect(() => {
    loadCandidates();
    loadPayoutHistory();
    loadFeeConfig();
  }, []);

  useEffect(() => {
    filterCandidates();
  }, [candidates, searchTerm, filterType]);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getPayoutCandidates();
      setCandidates(response.data.data || []);
    } catch (err) {
      console.error('Failed to load payout candidates:', err);
      setError(err.response?.data?.error || 'Failed to load payout candidates');
    } finally {
      setLoading(false);
    }
  };

  const loadPayoutHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await adminAPI.getPayoutHistory({ limit: 100 });
      setPayoutHistory(response.data.data || []);
    } catch (err) {
      console.error('Failed to load payout history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadFeeConfig = async () => {
    try {
      setLoadingFeeConfig(true);
      const response = await adminAPI.getFeeConfig();
      if (response.data.success && response.data.data) {
        setFeeConfig(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load fee configuration:', err);
      // Keep default values if loading fails
    } finally {
      setLoadingFeeConfig(false);
    }
  };

  const filterCandidates = () => {
    let filtered = candidates;

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(c => c.type === filterType);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => {
        // Search in basic fields
        const matchesBasic =
          c.name.toLowerCase().includes(term) ||
          c.email?.toLowerCase().includes(term) ||
          c.contact?.includes(term);

        // Search in Razorpay payment IDs
        const matchesPaymentIds = c.razorpay_payment_ids?.some(
          id => id.toLowerCase().includes(term)
        );

        // Search in settlement ID
        const matchesSettlementId = c.settlement_id?.toLowerCase().includes(term);

        return matchesBasic || matchesPaymentIds || matchesSettlementId;
      });
    }

    setFilteredCandidates(filtered);
  };

  const handleFeeConfigChange = (newConfig) => {
    setFeeConfig(newConfig);
    // Recalculate fees for all candidates
    recalculateCandidateFees(newConfig);
  };

  const recalculateCandidateFees = (config) => {
    const updatedCandidates = candidates.map(candidate => {
      const recipientKey = `${candidate.type}_${candidate.id}`;
      const applicableConfig = recipientFeeOverrides[recipientKey] || config;
      const feeBreakdown = calculateFees(candidate.available_balance, applicableConfig);

      return {
        ...candidate,
        fee_breakdown: feeBreakdown,
        fee_percentages: applicableConfig,
        net_amount: feeBreakdown.netAmount
      };
    });

    setCandidates(updatedCandidates);
  };

  const calculateFees = (grossAmount, feePercentages) => {
    // Handle null or undefined feePercentages
    if (!feePercentages) {
      return {
        grossAmount: parseFloat(grossAmount.toFixed(2)),
        pgFee: 0,
        gstAmount: 0,
        theraptrackFee: 0,
        totalFee: 0,
        netAmount: parseFloat(grossAmount.toFixed(2))
      };
    }

    const pgFee = (grossAmount * (feePercentages.pg_fee_percentage || 0)) / 100;
    const gstAmount = (pgFee * (feePercentages.gst_percentage || 0)) / 100;
    const theraptrackFee = (grossAmount * (feePercentages.theraptrack_fee_percentage || 0)) / 100;
    const totalFee = pgFee + gstAmount + theraptrackFee;
    const netAmount = grossAmount - totalFee;

    return {
      grossAmount: parseFloat(grossAmount.toFixed(2)),
      pgFee: parseFloat(pgFee.toFixed(2)),
      gstAmount: parseFloat(gstAmount.toFixed(2)),
      theraptrackFee: parseFloat(theraptrackFee.toFixed(2)),
      totalFee: parseFloat(totalFee.toFixed(2)),
      netAmount: parseFloat(netAmount.toFixed(2))
    };
  };

  const handleOpenFeeOverride = (candidate) => {
    setSelectedRecipientForFeeOverride(candidate);
    setShowFeeOverrideModal(true);
  };

  const handleSaveFeeOverride = (customConfig) => {
    if (!selectedRecipientForFeeOverride) return;

    const recipientKey = `${selectedRecipientForFeeOverride.type}_${selectedRecipientForFeeOverride.id}`;

    if (customConfig === null) {
      // Remove override - use common fees
      const newOverrides = { ...recipientFeeOverrides };
      delete newOverrides[recipientKey];
      setRecipientFeeOverrides(newOverrides);
    } else {
      // Save custom fees
      setRecipientFeeOverrides({
        ...recipientFeeOverrides,
        [recipientKey]: customConfig
      });
    }

    // Recalculate fees for the candidate
    const updatedCandidates = candidates.map(candidate => {
      if (candidate.id === selectedRecipientForFeeOverride.id &&
          candidate.type === selectedRecipientForFeeOverride.type) {
        const applicableConfig = customConfig || feeConfig;
        const feeBreakdown = calculateFees(candidate.available_balance, applicableConfig);

        return {
          ...candidate,
          fee_breakdown: feeBreakdown,
          fee_percentages: applicableConfig,
          net_amount: feeBreakdown.netAmount
        };
      }
      return candidate;
    });

    setCandidates(updatedCandidates);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSelectCandidate = (candidateId, candidateType) => {
    const candidate = candidates.find(c => c.id === candidateId && c.type === candidateType);
    if (!candidate || !candidate.eligible_for_payout) return;

    const key = `${candidate.type}_${candidate.id}`;
    setSelectedCandidates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const eligibleCandidates = filteredCandidates.filter(c => c.eligible_for_payout);
    const allSelected = eligibleCandidates.every(c => selectedCandidates.has(`${c.type}_${c.id}`));

    if (allSelected) {
      // Deselect all
      setSelectedCandidates(new Set());
    } else {
      // Select all eligible
      const newSet = new Set(selectedCandidates);
      eligibleCandidates.forEach(c => {
        newSet.add(`${c.type}_${c.id}`);
      });
      setSelectedCandidates(newSet);
    }
  };

  const getSelectedCandidatesData = () => {
    return Array.from(selectedCandidates).map(key => {
      const [type, id] = key.split('_');
      return candidates.find(c => c.id === parseInt(id) && c.type === type);
    }).filter(Boolean);
  };

  const handleInitiatePayout = () => {
    const selected = getSelectedCandidatesData();
    if (selected.length === 0) return;
    setShowConfirmModal(true);
  };

  const confirmPayout = async () => {
    const selected = getSelectedCandidatesData();
    if (selected.length === 0) return;

    try {
      setProcessing(true);
      setError('');
      setSuccess('');

      const recipientIds = selected.map(c => c.id);
      const recipientTypes = selected.map(c => c.type);

      const response = await adminAPI.createPayout({
        recipient_ids: recipientIds,
        recipient_types: recipientTypes,
        notes: payoutNotes || undefined,
        fee_config: feeConfig,
        recipient_fee_overrides: recipientFeeOverrides
      });

      if (response.data.errors && response.data.errors.length > 0) {
        setError(`${response.data.message}. Some payouts failed. Check details.`);
      } else {
        setSuccess(response.data.message || `Successfully initiated ${response.data.data.created.length} payout(s)`);
      }

      setShowConfirmModal(false);
      setSelectedCandidates(new Set());
      setPayoutNotes('');
      
      // Reload data
      await loadCandidates();
      await loadPayoutHistory();
    } catch (err) {
      console.error('Failed to create payout:', err);
      setError(err.response?.data?.error || 'Failed to initiate payout');
      setShowConfirmModal(false);
    } finally {
      setProcessing(false);
    }
  };

  const getTotalPayoutAmount = () => {
    const selected = getSelectedCandidatesData();
    return selected.reduce((sum, c) => sum + c.available_balance, 0);
  };

  const getTotalNetAmount = () => {
    const selected = getSelectedCandidatesData();
    return selected.reduce((sum, c) => sum + (c.net_amount || c.fee_breakdown?.netAmount || 0), 0);
  };

  const getTotalFees = () => {
    const selected = getSelectedCandidatesData();
    return selected.reduce((sum, c) => {
      if (c.fee_breakdown) {
        return sum + c.fee_breakdown.totalFee;
      }
      return sum;
    }, 0);
  };

  const handleExportPending = async () => {
    try {
      setError('');
      const response = await adminAPI.exportPendingPayouts();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pending-payouts-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSuccess('Pending payouts exported successfully');
    } catch (err) {
      console.error('Failed to export payouts:', err);
      setError('Failed to export payouts');
    }
  };

  const handleOpenCompleteModal = (payout) => {
    setSelectedPayoutForCompletion(payout);
    setManualReference('');
    setCompletionNotes('');
    setShowCompleteModal(true);
  };

  const handleCompletePayout = async () => {
    if (!manualReference.trim()) {
      setError('NEFT reference number is required');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      setSuccess('');

      await adminAPI.completeManualPayout(selectedPayoutForCompletion.id, {
        manual_reference: manualReference,
        completion_notes: completionNotes || undefined
      });

      setSuccess('Payout marked as completed successfully');
      setShowCompleteModal(false);
      setSelectedPayoutForCompletion(null);
      setManualReference('');
      setCompletionNotes('');

      // Reload data
      await loadPayoutHistory();
    } catch (err) {
      console.error('Failed to complete payout:', err);
      setError(err.response?.data?.error || 'Failed to complete payout');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenCancelModal = (payout) => {
    setSelectedPayoutForCancellation(payout);
    setCancellationReason('');
    setShowCancelModal(true);
  };

  const handleCancelPayout = async () => {
    try {
      setProcessing(true);
      setError('');
      setSuccess('');

      await adminAPI.cancelPayout(selectedPayoutForCancellation.id, {
        reason: cancellationReason || undefined
      });

      setSuccess('Payout cancelled successfully');
      setShowCancelModal(false);
      setSelectedPayoutForCancellation(null);
      setCancellationReason('');

      // Reload data
      await loadPayoutHistory();
      await loadCandidates();
    } catch (err) {
      console.error('Failed to cancel payout:', err);
      setError(err.response?.data?.error || 'Failed to cancel payout');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', label: 'Pending Transfer' },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', label: 'Completed' },
      cancelled: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading payout candidates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">Payouts Management</h1>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="card p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
            <CheckCircle className="h-5 w-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-border">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveSection('candidates')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'candidates'
                ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Payout Candidates
          </button>
          <button
            onClick={() => setActiveSection('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeSection === 'history'
                ? 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Payout History
          </button>
        </nav>
      </div>

      {/* Candidates Section */}
      {activeSection === 'candidates' && (
        <div className="space-y-4">
          {/* Fee Configuration Panel */}
          <FeeConfigurationPanel
            onConfigChange={handleFeeConfigChange}
            initialConfig={feeConfig}
          />

          {/* Filters and Actions */}
          <div className="card p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name, email, contact, payment ID, or settlement ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                  />
                </div>

                {/* Type Filter */}
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-secondary dark:text-dark-text-primary"
                >
                  <option value="all">All Types</option>
                  <option value="partner">Partners</option>
                  <option value="organization">Organizations</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleExportPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Export Pending (CSV)</span>
                </button>
                {getSelectedCandidatesData().length > 0 && (
                  <button
                    onClick={handleInitiatePayout}
                    disabled={processing}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <DollarSign className="h-5 w-5" />
                    <span>Initiate Payout ({getSelectedCandidatesData().length})</span>
                  </button>
                )}
              </div>
            </div>

            {/* Selected Summary */}
            {getSelectedCandidatesData().length > 0 && (
              <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-primary-900 dark:text-primary-200">
                      {getSelectedCandidatesData().length} candidate(s) selected
                    </span>
                    <div className="text-right">
                      <div className="text-xs text-primary-700 dark:text-primary-300">
                        Gross Amount: {formatCurrency(getTotalPayoutAmount())}
                      </div>
                      <div className="text-xs text-primary-700 dark:text-primary-300">
                        Fees: {formatCurrency(getTotalFees())}
                      </div>
                      <div className="text-lg font-bold text-primary-900 dark:text-primary-200">
                        Net Payout: {formatCurrency(getTotalNetAmount())}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Candidates Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center space-x-2"
                      >
                        {filteredCandidates.filter(c => c.eligible_for_payout).every(c =>
                          selectedCandidates.has(`${c.type}_${c.id}`)
                        ) && filteredCandidates.filter(c => c.eligible_for_payout).length > 0 ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Select
                        </span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Available Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Net Amount (After Fees)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Razorpay Payment ID(s)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Settlement ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Bank Account Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Bank Details (for NEFT)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Pending
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Withdrawn
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total Earnings
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-bg-primary divide-y divide-gray-200 dark:divide-dark-border">
                  {filteredCandidates.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="px-6 py-8 text-center text-gray-500">
                        No candidates found
                      </td>
                    </tr>
                  ) : (
                    filteredCandidates.map((candidate) => {
                      const key = `${candidate.type}_${candidate.id}`;
                      const isSelected = selectedCandidates.has(key);
                      const hasPendingPayout = candidate.pending_payout !== null && candidate.pending_payout !== undefined;
                      const isEligible = candidate.eligible_for_payout && !hasPendingPayout;

                      return (
                        <tr
                          key={key}
                          className={`
                            ${isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''}
                            ${hasPendingPayout ? 'bg-orange-50 dark:bg-orange-900/10' : ''}
                          `}
                        >
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleSelectCandidate(candidate.id, candidate.type)}
                              disabled={!isEligible}
                              className={!isEligible ? 'cursor-not-allowed opacity-50' : ''}
                            >
                              {isSelected ? (
                                <CheckSquare className="h-5 w-5 text-primary-600" />
                              ) : (
                                <Square className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                                {candidate.name}
                              </div>
                              {candidate.pending_payout && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                  Payout Pending
                                </span>
                              )}
                            </div>
                            {candidate.in_payout_amount > 0 && (
                              <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                In payout: {formatCurrency(candidate.in_payout_amount)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {candidate.type === 'partner' ? (
                                <User className="h-4 w-4 text-gray-400" />
                              ) : (
                                <Building2 className="h-4 w-4 text-gray-400" />
                              )}
                              <span className="text-sm text-gray-900 dark:text-dark-text-primary capitalize">
                                {candidate.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-bold ${
                              candidate.available_balance > 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-gray-400'
                            }`}>
                              {formatCurrency(candidate.available_balance)}
                            </div>
                            {!isEligible && candidate.total_earnings > 0 && !hasPendingPayout && (
                              <div className="text-xs text-gray-500">Not eligible</div>
                            )}
                            {hasPendingPayout && (
                              <div className="text-xs text-orange-600 dark:text-orange-400">Payout in progress</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {candidate.fee_breakdown ? (
                              <div>
                                <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                  {formatCurrency(candidate.net_amount || candidate.fee_breakdown.netAmount)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Fee: {formatCurrency(candidate.fee_breakdown.totalFee)}
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400">-</div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {candidate.razorpay_payment_ids && candidate.razorpay_payment_ids.length > 0 ? (
                              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1 max-h-24 overflow-y-auto">
                                {candidate.razorpay_payment_ids.map((paymentId, index) => (
                                  <div key={index} className="font-mono">
                                    {paymentId}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">-</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono text-gray-600 dark:text-gray-400">
                              {candidate.settlement_id || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {candidate.bank_account_verified ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Verified
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Not Verified
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {candidate.bank_account_verified && candidate.bank_account_number ? (
                              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                                <div><span className="font-medium">Holder:</span> {candidate.bank_account_holder_name || '-'}</div>
                                <div><span className="font-medium">Account:</span> {candidate.bank_account_number}</div>
                                <div><span className="font-medium">IFSC:</span> {candidate.bank_ifsc_code || '-'}</div>
                                <div><span className="font-medium">Bank:</span> {candidate.bank_name || '-'}</div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">Not available</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-dark-text-primary">
                              {formatCurrency(candidate.pending_earnings)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-dark-text-primary">
                              {formatCurrency(candidate.withdrawn_amount)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                              {formatCurrency(candidate.total_earnings)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <div>{candidate.email || '-'}</div>
                              <div className="text-xs">{candidate.contact || '-'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleOpenFeeOverride(candidate)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors"
                                title="Edit fee percentages"
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                Edit Fees
                              </button>
                              {recipientFeeOverrides[key] && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                  <Tag className="h-3 w-3 mr-1" />
                                  Custom
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* History Section */}
      {activeSection === 'history' && (
        <div className="card overflow-hidden">
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 text-primary-600 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Payment ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Settlement ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Gross Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Net Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Fees
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Reference / Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-bg-primary divide-y divide-gray-200 dark:divide-dark-border">
                  {payoutHistory.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                        No payout history found
                      </td>
                    </tr>
                  ) : (
                    payoutHistory.map((payout) => (
                      <tr key={payout.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-600 dark:text-gray-400">
                            #{payout.id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-600 dark:text-gray-400">
                            {payout.settlement_id || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-900 dark:text-dark-text-primary">
                              {formatDate(payout.payout_date)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                            {payout.recipient_name || 'Unknown'}
                          </div>
                          {payout.recipient_email && (
                            <div className="text-xs text-gray-500">{payout.recipient_email}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 dark:text-dark-text-primary capitalize">
                            {payout.recipient_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                            {formatCurrency(parseFloat(payout.gross_amount || payout.amount || 0))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                            {formatCurrency(parseFloat(payout.net_amount || payout.amount || 0))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <div>Fee: {formatCurrency(parseFloat(payout.transaction_fee || 0))}</div>
                            <div className="text-xs">GST: {formatCurrency(parseFloat(payout.gst_on_fee || 0))}</div>
                            {payout.metadata?.fee_percentages ? (
                              <div className="text-xs text-gray-500 mt-1" title="Fee Percentages">
                                PG: {payout.metadata.fee_percentages.pg_fee_percentage}% |
                                GST: {payout.metadata.fee_percentages.gst_percentage}% |
                                TT: {payout.metadata.fee_percentages.theraptrack_fee_percentage}%
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400 mt-1">Legacy (Fixed Fee)</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(payout.status)}
                        </td>
                        <td className="px-6 py-4">
                          {payout.status === 'completed' && payout.manual_reference ? (
                            <div className="text-xs space-y-1">
                              <div className="font-mono text-gray-900 dark:text-dark-text-primary">
                                {payout.manual_reference}
                              </div>
                              {payout.completed_at && (
                                <div className="text-gray-500">
                                  Completed: {formatDate(payout.completed_at)}
                                </div>
                              )}
                              {payout.completed_by_admin && (
                                <div className="text-gray-500">
                                  By: {payout.completed_by_admin.name}
                                </div>
                              )}
                            </div>
                          ) : payout.status === 'cancelled' ? (
                            <div className="text-xs text-gray-500">
                              <div>Cancelled</div>
                              {payout.notes && (
                                <div className="text-xs mt-1 italic">{payout.notes}</div>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">
                              Pending transfer
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {payout.status === 'pending' ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleOpenCompleteModal(payout)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center space-x-1"
                              >
                                <CheckCircle className="h-3 w-3" />
                                <span>Complete</span>
                              </button>
                              <button
                                onClick={() => handleOpenCancelModal(payout)}
                                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center space-x-1"
                              >
                                <XCircle className="h-3 w-3" />
                                <span>Cancel</span>
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
                Confirm Payout Initiation
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    You are about to initiate payouts for {getSelectedCandidatesData().length} candidate(s):
                  </p>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-dark-border rounded p-2">
                    <div className="space-y-2">
                      {getSelectedCandidatesData().map(candidate => {
                        const recipientKey = `${candidate.type}_${candidate.id}`;
                        const hasCustomFees = !!recipientFeeOverrides[recipientKey];
                        return (
                          <div key={recipientKey} className="text-sm bg-gray-50 dark:bg-dark-bg-tertiary p-2 rounded">
                            <div className="font-medium text-gray-900 dark:text-dark-text-primary flex items-center gap-2">
                              {candidate.name} ({candidate.type})
                              {hasCustomFees && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                  Custom Fees
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 space-y-0.5">
                              <div className="flex justify-between">
                                <span>Gross:</span>
                                <span>{formatCurrency(candidate.available_balance)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Fees:</span>
                                <span className="text-red-600 dark:text-red-400">
                                  -{formatCurrency(candidate.fee_breakdown?.totalFee || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between font-medium pt-0.5 border-t border-gray-200 dark:border-dark-border">
                                <span>Net:</span>
                                <span className="text-green-600 dark:text-green-400">
                                  {formatCurrency(candidate.net_amount || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                      <p className="font-medium mb-1">Manual NEFT Transfer Required</p>
                      <p className="text-xs">After creating payout records, you must manually transfer funds via NEFT to each recipient's bank account. Then mark each payout complete with the NEFT reference number.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Gross Amount:</span>
                    <span className="font-medium text-gray-900 dark:text-dark-text-primary">
                      {formatCurrency(getTotalPayoutAmount())}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Transaction Fees:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      -{formatCurrency(getTotalFees())}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-dark-border pt-2 flex justify-between">
                    <span className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">
                      Net Payout Amount:
                    </span>
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                      {formatCurrency(getTotalNetAmount())}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={payoutNotes}
                    onChange={(e) => setPayoutNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
                    placeholder="Add any notes for this payout..."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPayoutNotes('');
                  }}
                  disabled={processing}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPayout}
                  disabled={processing}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {processing ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Confirm</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Payout Modal */}
      {showCompleteModal && selectedPayoutForCompletion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                  Mark Payout Complete
                </h3>
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg p-3">
                  <div className="text-sm space-y-1">
                    <div><span className="font-medium">Recipient:</span> {selectedPayoutForCompletion.recipient_name}</div>
                    <div><span className="font-medium">Amount:</span> {formatCurrency(selectedPayoutForCompletion.net_amount)}</div>
                    <div><span className="font-medium">Date:</span> {formatDate(selectedPayoutForCompletion.payout_date)}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    NEFT Reference Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={manualReference}
                    onChange={(e) => setManualReference(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
                    placeholder="Enter NEFT/UTR reference number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Completion Notes (optional)
                  </label>
                  <textarea
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
                    placeholder="Add any notes about the completion..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCompleteModal(false);
                    setManualReference('');
                    setCompletionNotes('');
                  }}
                  disabled={processing}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompletePayout}
                  disabled={processing || !manualReference.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {processing ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Mark Complete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Payout Modal */}
      {showCancelModal && selectedPayoutForCancellation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                  Cancel Payout
                </h3>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg p-3">
                  <div className="text-sm space-y-1">
                    <div><span className="font-medium">Recipient:</span> {selectedPayoutForCancellation.recipient_name}</div>
                    <div><span className="font-medium">Amount:</span> {formatCurrency(selectedPayoutForCancellation.net_amount)}</div>
                    <div><span className="font-medium">Date:</span> {formatDate(selectedPayoutForCancellation.payout_date)}</div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                      <p className="font-medium">Warning</p>
                      <p className="text-xs mt-1">Linked earnings will revert to "available" status and the candidate will become eligible for payout again.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cancellation Reason (optional)
                  </label>
                  <textarea
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary"
                    placeholder="Why is this payout being cancelled?"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancellationReason('');
                  }}
                  disabled={processing}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary disabled:opacity-50"
                >
                  Close
                </button>
                <button
                  onClick={handleCancelPayout}
                  disabled={processing}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {processing ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      <span>Cancel Payout</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fee Override Modal */}
      <FeeOverrideModal
        isOpen={showFeeOverrideModal}
        onClose={() => setShowFeeOverrideModal(false)}
        recipient={selectedRecipientForFeeOverride}
        currentFeeConfig={feeConfig}
        onSave={handleSaveFeeOverride}
      />
    </div>
  );
};

export default PayoutsTab;

